import { ColumnFilter } from '@/common/components/table/ColumnFilter';
import { ColumnHeader } from '@/common/components/table/ColumnHeader';
import {
  MultiSelectFilter,
  MultiSelectFilterOption,
} from '@/common/components/table/MultiSelectFilter';
import {
  SelectFilter,
  SelectFilterOption,
} from '@/common/components/table/SelectFilter';
import { SortButton } from '@/common/components/table/SortButton';

type BaseColumnConfig<TSortField extends string> = {
  field: TSortField | null;
  label: string;
  sortable: boolean;
  width?: string;
};

type NonFilterableColumn<TSortField extends string> =
  BaseColumnConfig<TSortField> & {
    filterable: false;
  };

type TextFilterColumn<TSortField extends string> =
  BaseColumnConfig<TSortField> & {
    filterable: true;
    filterType: 'text';
    filterKey: string;
    filterPlaceholder?: string;
  };

type SelectFilterColumn<TSortField extends string> =
  BaseColumnConfig<TSortField> & {
    filterable: true;
    filterType: 'select';
    filterKey: string;
    filterPlaceholder?: string;
    options: SelectFilterOption[];
  };

type MultiSelectFilterColumn<TSortField extends string> =
  BaseColumnConfig<TSortField> & {
    filterable: true;
    filterType: 'multiselect';
    filterKey: string;
    filterPlaceholder?: string;
    options: MultiSelectFilterOption[];
  };

export type ColumnConfig<TSortField extends string> =
  | NonFilterableColumn<TSortField>
  | TextFilterColumn<TSortField>
  | SelectFilterColumn<TSortField>
  | MultiSelectFilterColumn<TSortField>;

interface TableColumnHeaderProps<
  TSortField extends string,
  TFilters extends Record<string, string | string[]>,
> {
  column: ColumnConfig<TSortField>;
  sortBy: TSortField;
  sortOrder: 'asc' | 'desc';
  filters: TFilters;
  onSort: (field: TSortField) => void;
  onFilterChange: (field: string, value: string | string[]) => void;
}

export const TableColumnHeader = <
  TSortField extends string,
  TFilters extends Record<string, string | string[]>,
>({
  column,
  sortBy,
  sortOrder,
  filters,
  onSort,
  onFilterChange,
}: TableColumnHeaderProps<TSortField, TFilters>) => {
  const filterValue = column.filterable ? filters[column.filterKey] : undefined;
  const hasActiveFilter =
    column.filterable &&
    ((Array.isArray(filterValue) && filterValue.length > 0) ||
      (!Array.isArray(filterValue) && filterValue !== ''));
  const hasActiveSort =
    column.sortable && column.field !== null && sortBy === column.field;

  const renderFilterContent = (onClose?: () => void) => {
    if (!column.filterable) return null;

    if (column.filterType === 'text') {
      return (
        <ColumnFilter
          value={filterValue as string}
          onChange={(value) => onFilterChange(column.filterKey, value)}
          placeholder={column.filterPlaceholder}
        />
      );
    } else if (column.filterType === 'multiselect') {
      return (
        <MultiSelectFilter
          values={filterValue as string[]}
          onChange={(values) => onFilterChange(column.filterKey, values)}
          options={column.options}
          placeholder={column.filterPlaceholder}
        />
      );
    } else {
      return (
        <SelectFilter
          value={filterValue as string}
          onChange={(value) => onFilterChange(column.filterKey, value)}
          options={column.options}
          placeholder={column.filterPlaceholder}
          onClose={onClose}
        />
      );
    }
  };

  const renderSortButton = () => {
    if (!column.sortable || !column.field) return null;

    return (
      <SortButton
        field={column.field}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={onSort}
      />
    );
  };

  return (
    <ColumnHeader
      label={column.label}
      sortable={column.sortable}
      filterable={column.filterable}
      hasActiveFilter={hasActiveFilter}
      hasActiveSort={hasActiveSort}
      renderFilterContent={renderFilterContent}
      renderSortButton={renderSortButton}
      width={column.width}
    />
  );
};
